import type { ContentSearchPolicy } from '@content-search/domain/ClassificationResults';
import type { PolicyResult, PolicyEvaluation } from '@content-search/domain/PolicyResult';

/**
 * Calculates the overall policy result from triggered and non-triggered policies
 *
 * @param policies - Array of policies that were triggered (content violates these policies)
 * @param nonTriggeredPolicies - Array of policies that were not triggered (content complies with these policies)
 * @returns PolicyResult object with compliance status and individual policy evaluations
 */
export function calculatePolicyResult(policies?: ContentSearchPolicy[], nonTriggeredPolicies?: ContentSearchPolicy[]): PolicyResult | undefined {
  const triggeredPolicies = policies || [];
  const nonTriggered = nonTriggeredPolicies || [];

  const totalPolicies = triggeredPolicies.length + nonTriggered.length;

  // If no policies were evaluated, return undefined
  if (totalPolicies === 0) {
    return undefined;
  }

  // Create policy evaluations for triggered policies (rejected)
  const triggeredEvaluations: PolicyEvaluation[] = triggeredPolicies.map(policy => ({
    policyId: policy.id,
    policyName: policy.name,
    isAllowed: false, // Content violates this policy
    reason: policy.description || undefined,
  }));

  // Create policy evaluations for non-triggered policies (allowed)
  const nonTriggeredEvaluations: PolicyEvaluation[] = nonTriggered.map(policy => ({
    policyId: policy.id,
    policyName: policy.name,
    isAllowed: true, // Content complies with this policy
    reason: policy.description || undefined,
  }));

  // Combine all evaluations
  const policyEvaluations = [...triggeredEvaluations, ...nonTriggeredEvaluations];

  // Content is compliant only if no policies were triggered (all policies allow it)
  const isCompliant = triggeredPolicies.length === 0;

  return {
    isCompliant,
    totalPoliciesEvaluated: totalPolicies,
    allowedCount: nonTriggered.length,
    rejectedCount: triggeredPolicies.length,
    policyEvaluations,
  };
}
